import React, { useMemo } from 'react';
import { formatNumber } from '../../../utils/commonFunction';

const SEGMENTS = [
    {
        key: 'locked',
        label: 'Locked',
        barClass: 'bg-teal-600 dark:bg-teal-500',
        indicatorClass: 'bg-teal-600 dark:bg-teal-500',
        cardClass:
            'border-teal-200/60 bg-teal-50/50 dark:border-teal-900/50 dark:bg-teal-950/30',
    },
    {
        key: 'released',
        label: 'Released',
        barClass: 'bg-teal-400 dark:bg-teal-300',
        indicatorClass: 'bg-teal-400 dark:bg-teal-300',
        cardClass:
            'border-teal-200/40 bg-teal-50/30 dark:border-teal-800/40 dark:bg-teal-950/20',
    },
    {
        key: 'refunded',
        label: 'Refunded',
        barClass: 'bg-amber-400 dark:bg-amber-500/90',
        indicatorClass: 'bg-amber-400 dark:bg-amber-500/90',
        cardClass:
            'border-amber-200/50 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/20',
    },
];

function formatEscrowValue(value) {
    return formatNumber(value);
}

const EscrowStatCard = ({ label, value, tokenSymbol, indicatorClass, cardClass }) => (
    <div className={`flex min-h-[120px] flex-col rounded-xl border p-4 ${cardClass}`}>
        <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${indicatorClass}`} aria-hidden />
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
            {label}
        </p>
        <p className="mt-2 text-2xl font-bold tabular-nums leading-none text-zinc-900 dark:text-white sm:text-3xl">
            {value}
        </p>
        <p className="mt-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-500">{tokenSymbol}</p>
    </div>
);

const EscrowFundDistribution = ({
    locked = 0,
    released = 0,
    refunded = 0,
    tokenSymbol = 'USDC',
}) => {
    const amounts = useMemo(
        () => ({
            locked: Number(locked) || 0,
            released: Number(released) || 0,
            refunded: Number(refunded) || 0,
        }),
        [locked, released, refunded],
    );

    const total = amounts.locked + amounts.released + amounts.refunded;
    const hasFunds = total > 0;

    const barWidths = useMemo(() => {
        if (!hasFunds) {
            return { locked: 0, released: 0, refunded: 0 };
        }
        return {
            locked: (amounts.locked / total) * 100,
            released: (amounts.released / total) * 100,
            refunded: (amounts.refunded / total) * 100,
        };
    }, [amounts, hasFunds, total]);

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
            <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                Escrow fund distribution
            </h3>

            <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800/80">
                {hasFunds ? (
                    SEGMENTS.map((seg) => {
                        const width = barWidths[seg.key];
                        if (width <= 0) return null;
                        return (
                            <div
                                key={seg.key}
                                className={`${seg.barClass} transition-all duration-500`}
                                style={{ width: `${width}%` }}
                                title={`${seg.label} ${Math.round(width)}%`}
                            />
                        );
                    })
                ) : (
                    <div className="w-full bg-zinc-200 dark:bg-zinc-700/50" />
                )}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {SEGMENTS.map((seg) => (
                    <EscrowStatCard
                        key={seg.key}
                        label={seg.label}
                        value={formatEscrowValue(amounts[seg.key])}
                        tokenSymbol={tokenSymbol}
                        indicatorClass={seg.indicatorClass}
                        cardClass={seg.cardClass}
                    />
                ))}
            </div>

            {!hasFunds ? (
                <p className="mt-3 text-center text-xs text-zinc-500 dark:text-zinc-500">
                    No escrow funds yet — amounts appear when tasks are funded.
                </p>
            ) : null}
        </div>
    );
};

export default EscrowFundDistribution;
